/* `?worker&url` for the reason given in `createPcmCapture`. */
import playbackWorkletUrl from './realtimeVoice.playback.worklet.ts?worker&url';
import {
  REALTIME_VOICE_WARM_QUERY,
  type RealtimeAgentState,
  type RealtimeClientEvent,
  type RealtimeServerEvent,
} from '@wundo/shared/realtimeVoice.ts';
import { RealtimeHandover } from '@wundo/shared/realtimeHandover.ts';
import { createPcmCapture, PCM_SAMPLE_RATE } from './createPcmCapture.ts';
import { hangUpVoiceSocket } from './hangUpVoiceSocket.ts';
import { openChosenMicrophone } from './openChosenMicrophone.ts';
import { RealtimeCaption } from './realtimeCaption.ts';
import type { PlaybackGapSummary } from './realtimePlaybackGaps.ts';
import { voiceMicrophoneConstraints } from './voiceMicrophoneConstraints.ts';

const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 10_000;

/**
 * Refusals: the session is not this reader's, or a second tab has taken the
 * call over. Neither is answered by connecting again.
 */
const REFUSED_CLOSE_CODES = [4403, 4409];

/** How much a reading has to move before the orb is asked to redraw. */
const LEVEL_STEP = 0.02;

/** What the character is doing, plus the state of having no answer yet. */
export type RealtimeVoiceState = RealtimeAgentState | 'connecting';

/** One 20 ms chunk of microphone, with a loudness reading now and then. */
export interface RealtimeCaptureMessage {
  pcm: Int16Array<ArrayBuffer>;
  /** Null on the chunks between readings. */
  level: number | null;
}

export type RealtimePlaybackCommand =
  | { type: 'audio'; pcm: Int16Array }
  /** The character has started a new turn: count its playback from zero. */
  | { type: 'mark'; mark: number; itemId: string }
  /** No more audio is coming for this turn; what is queued still plays. */
  | { type: 'end' }
  /** Drop what is queued and say how much of the turn was heard. */
  | { type: 'clear' };

export type RealtimePlaybackReport =
  /** How much of the turn was heard when playback was cleared. */
  | { type: 'played'; samples: number }
  /** How far playback has got since `mark`, a few times a second. */
  | { type: 'progress'; mark: number; samples: number }
  /** The queue ran dry after `chunks` audio chunks had arrived in total. */
  | { type: 'drained'; chunks: number }
  /** Once per turn, when its playback ends. */
  | { type: 'gaps'; summary: PlaybackGapSummary };

export interface RealtimeVoiceListener {
  onState: (state: RealtimeVoiceState) => void;
  onLevel: (level: number) => void;
  /** What the character has said so far, as far as its voice has played. */
  onCaption: (text: string) => void;
  onSuggestEnd: () => void;
  /** The character said goodbye and hung up, and its last words have played. */
  onEnded: () => void;
  /** The character's voice failed on one answer; the call goes on. */
  onVoiceTrouble: () => void;
  onError: (message: string) => void;
  /** The call is over and is not coming back on its own. */
  onClosed: () => void;
}

export interface RealtimeVoiceOptions {
  /** The bridge's WebSocket address, as the start of the session handed it out. */
  url: string;
  microphoneId: string | null;
  speakerId: string | null;
}

/**
 * Chrome can send a whole AudioContext to a chosen speaker. The DOM lib has no
 * word for it yet, and the browsers that cannot do it simply do not have it.
 */
interface SinkSelectableAudioContext extends AudioContext {
  setSinkId?: (deviceId: string) => Promise<void>;
}

/**
 * A spoken practice conversation over one socket to our API.
 *
 * There is no peer connection and no room: the microphone goes up as PCM and
 * the character's voice comes back as PCM, both over the same socket, and
 * everything either side knows about the conversation travels beside them as
 * JSON. Audio is converted on the audio thread — see the two worklets — so a
 * busy page cannot stutter the call.
 *
 * A dropped line is not a dropped conversation. The socket reconnects on its
 * own and the bridge builds the model's session back from what was said, so the
 * microphone, the speaker and the audio graph are set up once and survive it.
 */
export class RealtimeVoiceClient {
  private readonly url: string;
  private readonly listener: RealtimeVoiceListener;
  private readonly caption: RealtimeCaption;
  private readonly handover: RealtimeHandover;

  private microphoneId: string | null;
  private speakerId: string | null;

  private socket: WebSocket | null = null;
  /** The second socket, on a newer task, until it takes the call over. */
  private warmSocket: WebSocket | null = null;
  /** The socket that was authoritative until the swap, waiting to be closed. */
  private retiringSocket: WebSocket | null = null;
  private context: SinkSelectableAudioContext | null = null;
  private stream: MediaStream | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private capture: AudioWorkletNode | null = null;
  private playback: AudioWorkletNode | null = null;

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private attempts = 0;
  private stopped = false;
  private failed = false;
  private ready = false;
  private warmIsReady = false;
  private sentChunks = 0;
  private drainedChunks = 0;
  /**
   * The bridge says `listening` once it has sent the last audio, which is
   * seconds before it has played; the screen waits for the voice to finish.
   */
  private listeningAfterPlayback = false;
  /** The character hung up; the call ends once its goodbye has played. */
  private endedByCharacter = false;
  private microphoneOn = true;
  private level = 0;

  constructor(options: RealtimeVoiceOptions, listener: RealtimeVoiceListener) {
    this.url = options.url;
    this.microphoneId = options.microphoneId;
    this.speakerId = options.speakerId;
    this.listener = listener;
    this.caption = new RealtimeCaption((text) => listener.onCaption(text));
    this.handover = new RealtimeHandover({
      openWarm: () => this.openWarmSocket(),
      replay: (chunks) => this.replayToWarm(chunks),
      sendHandover: () => this.send(this.socket, { type: 'handover' }),
      sendPromote: (cutBytes) => this.promoteWarmSocket(cutBytes),
      closeOld: () => this.closeRetiringSocket(),
    });
  }

  /**
   * Take the microphone, build the audio graph and open the socket.
   *
   * Rejects when the microphone is refused, which is a different failure from a
   * call that cannot connect — the screen says so.
   */
  async connect(): Promise<void> {
    this.listener.onState('connecting');
    this.stream = await openChosenMicrophone(
      this.microphoneId,
      voiceMicrophoneConstraints,
    );

    /*
     * The AudioContext is made here rather than at construction because a
     * browser only lets a page make noise off a gesture, and the Start button
     * is the one this call has.
     */
    const context: SinkSelectableAudioContext = new AudioContext({
      sampleRate: PCM_SAMPLE_RATE,
    });
    this.context = context;

    await context.audioWorklet.addModule(playbackWorkletUrl);
    await context.resume();
    await this.playThroughSpeaker();

    this.playback = new AudioWorkletNode(context, 'realtime-playback', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });
    this.playback.port.onmessage = (event: MessageEvent<RealtimePlaybackReport>) => {
      this.handlePlayback(event.data);
    };
    this.playback.connect(context.destination);

    this.capture = await createPcmCapture(context, (message) => this.sendAudio(message));

    this.attachMicrophone();

    /* Hung up while the browser was still handing the microphone over. */
    if (this.stopped) {
      await this.disconnect();
      return;
    }

    this.openSocket();
  }

  /** Muted here rather than at the bridge: nothing should leave the machine. */
  setMicrophoneEnabled(enabled: boolean): void {
    this.microphoneOn = enabled;
    this.stream?.getAudioTracks().forEach((track) => {
      track.enabled = enabled;
    });
  }

  /**
   * Switch microphones mid-conversation, without touching the call itself.
   * A device picked just now is asked for exactly: if it is gone, the one
   * already running stays rather than the default taking over unseen.
   */
  async setMicrophone(deviceId: string): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: voiceMicrophoneConstraints(deviceId),
    });
    this.microphoneId = deviceId;

    this.releaseMicrophone();
    this.stream = stream;
    this.setMicrophoneEnabled(this.microphoneOn);
    this.attachMicrophone();
  }

  async setSpeaker(deviceId: string): Promise<void> {
    this.speakerId = deviceId;
    await this.playThroughSpeaker();
  }

  /** Hang up for good: nothing here reconnects afterwards. */
  async disconnect(): Promise<void> {
    this.stopped = true;
    this.ready = false;
    this.warmIsReady = false;
    this.handover.dispose();

    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    const socket = this.socket;
    this.socket = null;

    const warmSocket = this.warmSocket;
    this.warmSocket = null;
    warmSocket?.close(1_000);

    this.releaseMicrophone();
    this.source?.disconnect();
    this.capture?.disconnect();
    this.playback?.disconnect();
    this.source = null;
    this.capture = null;
    this.playback = null;

    await hangUpVoiceSocket(socket);

    const context = this.context;
    this.context = null;
    await context?.close().catch(() => undefined);
  }

  private attachMicrophone(): void {
    if (this.context === null || this.stream === null || this.capture === null) {
      return;
    }

    this.source?.disconnect();
    this.source = this.context.createMediaStreamSource(this.stream);
    this.source.connect(this.capture);
  }

  private releaseMicrophone(): void {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
  }

  /**
   * Never fatal: a browser that cannot switch outputs still has a working call
   * on whatever it was already using.
   */
  private async playThroughSpeaker(): Promise<void> {
    const context = this.context;
    if (context === null || this.speakerId === null || context.setSinkId === undefined) {
      return;
    }

    try {
      await context.setSinkId(this.speakerId);
    } catch {
      /* The call keeps playing where it was. */
    }
  }

  private openSocket(): void {
    /* A fresh line to the call starts no handover from where the last one stood. */
    this.abandonWarmSocket();

    const socket = new WebSocket(this.url);
    socket.binaryType = 'arraybuffer';
    this.socket = socket;
    this.handover.liveOpened();

    socket.onopen = () => {
      this.attempts = 0;
    };

    this.wireLiveSocket(socket);
  }

  /** What the authoritative socket does, whether it was opened here or promoted. */
  private wireLiveSocket(socket: WebSocket): void {
    socket.onmessage = (event: MessageEvent<unknown>) => {
      this.receive(event.data);
    };

    socket.onclose = (event) => {
      /* A socket we have already replaced has nothing left to say. */
      if (this.socket !== socket) {
        return;
      }

      this.socket = null;

      /* With a warm socket ready this is the switch, not a dropped call. */
      if (event.code !== 1_000 && this.handover.canPromote) {
        this.handover.oldSocketLost();
        return;
      }

      this.handleClose(event.code);
    };
  }

  /**
   * A second socket that lands on a newer task and hears the microphone too.
   *
   * It carries no audio to the speaker and writes nothing down until it is
   * promoted, so the call underneath it never notices that it exists.
   */
  private openWarmSocket(): void {
    if (this.stopped) {
      return;
    }

    const separator = this.url.includes('?') ? '&' : '?';
    const socket = new WebSocket(`${this.url}${separator}${REALTIME_VOICE_WARM_QUERY}`);
    socket.binaryType = 'arraybuffer';
    this.warmSocket = socket;
    this.warmIsReady = false;

    socket.onopen = () => {
      this.handover.warmOpened();
    };

    socket.onmessage = (event: MessageEvent<unknown>) => {
      this.receiveWarm(event.data);
    };

    socket.onclose = () => {
      if (this.warmSocket !== socket) {
        return;
      }

      this.warmSocket = null;
      this.warmIsReady = false;
      /* Refused by an equally old task or simply dropped: try again in a moment. */
      this.handover.warmRefused();
    };
  }

  private abandonWarmSocket(): void {
    const warmSocket = this.warmSocket;
    this.warmSocket = null;
    this.warmIsReady = false;
    warmSocket?.close(1_000);
  }

  /** Nothing a warm socket says is believed, and nothing it sends is heard. */
  private receiveWarm(data: unknown): void {
    if (typeof data !== 'string') {
      return;
    }

    let event: RealtimeServerEvent;
    try {
      event = JSON.parse(data) as RealtimeServerEvent;
    } catch {
      return;
    }

    if (event.type === 'ready') {
      this.warmIsReady = true;
      this.handover.warmReady();
      return;
    }

    /* A warm socket that cannot start is no use; another task may do better. */
    if (event.type === 'error') {
      this.warmSocket?.close(1_000);
    }
  }

  private replayToWarm(chunks: Uint8Array<ArrayBuffer>[]): void {
    const warmSocket = this.warmSocket;
    if (warmSocket?.readyState !== WebSocket.OPEN) {
      return;
    }

    chunks.forEach((chunk) => warmSocket.send(chunk));
  }

  /**
   * The warm socket becomes the call, without a break anybody can hear: the
   * playback queue keeps draining and the screen is never told to wait.
   */
  private promoteWarmSocket(cutBytes: number | undefined): void {
    const warmSocket = this.warmSocket;
    if (warmSocket === null) {
      return;
    }

    this.send(warmSocket, { type: 'promote', cut_bytes: cutBytes });

    this.retiringSocket = this.socket;
    this.socket = warmSocket;
    this.warmSocket = null;
    this.warmIsReady = false;
    this.attempts = 0;
    this.wireLiveSocket(warmSocket);
  }

  private closeRetiringSocket(): void {
    const socket = this.retiringSocket;
    this.retiringSocket = null;
    if (socket === null) {
      return;
    }

    /* Left wired, its close would be read as a call that needs reconnecting. */
    socket.onmessage = null;
    socket.onclose = null;
    socket.close(1_000);
  }

  private receive(data: unknown): void {
    if (data instanceof ArrayBuffer) {
      this.play(data);
      return;
    }

    if (typeof data !== 'string') {
      return;
    }

    let event: RealtimeServerEvent;
    try {
      event = JSON.parse(data) as RealtimeServerEvent;
    } catch {
      /* Anything that is not a shape we know is ignored rather than guessed at. */
      return;
    }

    this.handleEvent(event);
  }

  private handleEvent(event: RealtimeServerEvent): void {
    switch (event.type) {
      case 'ready':
        this.ready = true;
        this.listeningAfterPlayback = false;
        this.listener.onState('listening');
        return;
      case 'agent_state':
        this.handleAgentState(event.state, event.item_id);
        return;
      case 'interrupt':
        this.clearPlayback();
        this.settleListening();
        return;
      case 'transcript_delta':
        this.caption.addText(event.item_id, event.delta);
        return;
      case 'transcript':
        /* The caption is built from the deltas, in step with the voice. */
        return;
      case 'turn_end':
        this.handover.turnEnded(event.since_bytes, event.end_bytes);
        return;
      case 'prepare_reconnect':
        this.handover.prepareRequested();
        return;
      case 'settled':
        this.handover.settledBefore(event.since_bytes);
        return;
      case 'released':
        this.handover.released(event.cut_bytes);
        return;
      case 'suggest_end':
        this.listener.onSuggestEnd();
        return;
      case 'ended':
        this.endedByCharacter = true;
        this.settleEnded();
        return;
      case 'tts_error':
        this.listener.onVoiceTrouble();
        return;
      case 'reconnect':
        /* With a warm socket ready, the old task going away is the switch. */
        if (this.handover.canPromote) {
          this.handover.oldSocketLost();
          return;
        }
        this.reopen();
        return;
      case 'error':
        this.failed = true;
        this.listener.onError(event.message);
        return;
    }
  }

  private handleAgentState(state: RealtimeAgentState, itemId: string | undefined): void {
    if (state === 'speaking') {
      const mark = this.caption.startAnswer(itemId ?? '');
      this.command({ type: 'mark', mark, itemId: itemId ?? '' });
    }

    /* The bridge says `listening` once an answer is done, so no more of its audio is coming. */
    if (state === 'listening') {
      this.command({ type: 'end' });
      this.caption.complete();
      this.listeningAfterPlayback = true;
      this.settleListening();
      return;
    }

    this.listeningAfterPlayback = false;
    this.listener.onState(state);
  }

  private settleListening(): void {
    if (!this.listeningAfterPlayback || this.drainedChunks !== this.sentChunks) {
      return;
    }

    this.listeningAfterPlayback = false;
    this.listener.onState('listening');
  }

  private settleEnded(): void {
    if (!this.endedByCharacter || this.drainedChunks !== this.sentChunks) {
      return;
    }

    this.endedByCharacter = false;
    this.listener.onEnded();
  }

  private clearPlayback(): void {
    this.caption.stop();
    this.command({ type: 'clear' });
    this.drainedChunks = this.sentChunks;
  }

  /** The API task is going away. Drop this socket and pick up a new one. */
  private reopen(): void {
    this.ready = false;
    this.listeningAfterPlayback = false;
    this.listener.onState('connecting');
    this.clearPlayback();

    const socket = this.socket;
    this.socket = null;
    socket?.close(1_000);

    this.scheduleReconnect();
  }

  private handleClose(code: number): void {
    this.ready = false;
    this.listeningAfterPlayback = false;

    if (this.stopped) {
      return;
    }

    if (this.failed || REFUSED_CLOSE_CODES.includes(code)) {
      if (!this.failed) {
        this.listener.onError(`Practice call refused (${code})`);
      }
      this.listener.onClosed();
      return;
    }

    /* A clean close from the bridge is the end of the conversation. */
    if (code === 1_000) {
      this.listener.onClosed();
      return;
    }

    this.listener.onState('connecting');
    this.clearPlayback();
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    this.attempts += 1;
    const delay = Math.min(
      INITIAL_RECONNECT_DELAY_MS * 2 ** (this.attempts - 1),
      MAX_RECONNECT_DELAY_MS,
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.stopped) {
        this.openSocket();
      }
    }, delay);
  }

  private sendAudio(message: RealtimeCaptureMessage): void {
    if (message.level !== null) {
      this.reportLevel(message.level);
    }

    /* Before `ready` there is no model session on the other end to append to. */
    if (!this.ready || this.socket?.readyState !== WebSocket.OPEN) {
      return;
    }

    const pcm = message.pcm.buffer;
    this.socket.send(pcm);
    this.handover.audioChunk(new Uint8Array(pcm));

    if (this.warmIsReady && this.warmSocket?.readyState === WebSocket.OPEN) {
      this.warmSocket.send(pcm);
    }
  }

  private reportLevel(level: number): void {
    if (Math.abs(level - this.level) < LEVEL_STEP) {
      return;
    }

    this.level = level;
    this.listener.onLevel(level);
  }

  private play(data: ArrayBuffer): void {
    this.caption.addAudio(this.toMs(data.byteLength / Int16Array.BYTES_PER_ELEMENT));
    this.sentChunks += 1;
    this.playback?.port.postMessage(
      { type: 'audio', pcm: new Int16Array(data) } satisfies RealtimePlaybackCommand,
      [data],
    );
  }

  private handlePlayback(report: RealtimePlaybackReport): void {
    if (report.type === 'progress') {
      this.caption.played(report.mark, this.toMs(report.samples));
      return;
    }

    if (report.type === 'drained') {
      this.drainedChunks = report.chunks;
      this.settleListening();
      this.settleEnded();
      return;
    }

    if (report.type === 'gaps') {
      this.reportGaps(report.summary);
      return;
    }

    this.reportPlayed(report.samples);
  }

  private toMs(samples: number): number {
    return (samples / PCM_SAMPLE_RATE) * 1_000;
  }

  /**
   * How much of the character's turn was actually heard.
   *
   * Milliseconds rather than a position in a turn: the bridge is the only side
   * that knows which item is speaking, and it counts from the moment it said so.
   */
  private reportPlayed(samples: number): void {
    this.send(this.socket, {
      type: 'played',
      item_id: '',
      ms: Math.round(this.toMs(samples)),
    });
  }

  private reportGaps(summary: PlaybackGapSummary): void {
    this.send(this.socket, {
      type: 'playback_gaps',
      item_id: summary.itemId,
      played_ms: summary.playedMs,
      gaps: summary.gaps,
      gap_ms: summary.gapMs,
      longest_gap_ms: summary.longestGapMs,
      offsets_ms: summary.offsetsMs,
      end: summary.end,
    });
  }

  private send(socket: WebSocket | null, event: RealtimeClientEvent): void {
    if (socket?.readyState !== WebSocket.OPEN) {
      return;
    }

    socket.send(JSON.stringify(event));
  }

  private command(command: RealtimePlaybackCommand): void {
    this.playback?.port.postMessage(command);
  }
}
